import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Confirm,
  Loading,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetOne,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { performStatusTransition } from '../actions/performStatusTransition';
import { marketsForScope } from '../dataProvider/types';
import type {
  AdminDataProvider,
  Market,
  Merchant,
  OfflineStore,
  OfflineStoreStatus,
  TrackingMethod,
} from '../dataProvider';
import {
  OFFLINE_TRACKING_METHODS,
  trackingMethodLabel,
} from './offlineStoreModel';

/**
 * The offline venue authoring form (D-offline-stores). The descriptive-CRUD pattern PLUS
 * the offline-specific sections: the physical LOCATION (flat addressLine / postcode /
 * country, the venue's country), the offline TRACKING (clo / ob, 'ob' = Open Banking), and
 * the CLO matching CONFIG (mcc / mid? / isChain), distinct from the affiliate model. An
 * OPTIONAL merchantId links to an online brand (standalone when null). SAVE writes the
 * fields; the deactivate / activate TOGGLE flips status through the chokepoint (stores:write),
 * audited. AUTHORING only (the CLO crediting is the backend). Single-market per venue.
 */

const STANDALONE = '__standalone__';

const OfflineStoreFormInner = ({ record }: { record?: OfflineStore }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { activeRegion } = useRegion();
  const { role, accessibleRegions } = useAccessContext();

  const isEdit = record != null;
  // Single-market per venue (a physical shop is in one country); the in-scope markets, no 'all'.
  const marketChoices = useMemo(
    () => marketsForScope(activeRegion ?? 'GLOBAL'),
    [activeRegion],
  );

  // The online brands, for the OPTIONAL merchant link (reuse a catalog brand's identity).
  const { data: merchants } = useGetList<Merchant>('merchants', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'name', order: 'ASC' },
  });

  const [name, setName] = useState(record?.name ?? '');
  const [merchantId, setMerchantId] = useState<string>(
    record?.merchantId != null ? String(record.merchantId) : STANDALONE,
  );
  const [addressLine, setAddressLine] = useState(record?.addressLine ?? '');
  const [postcode, setPostcode] = useState(record?.postcode ?? '');
  const [country, setCountry] = useState(record?.country ?? '');
  const [market, setMarket] = useState<Market>(
    record?.market ?? marketChoices[0] ?? 'uk',
  );
  const [trackingMethod, setTrackingMethod] = useState<TrackingMethod>(
    record?.trackingMethod ?? 'clo',
  );
  const [mcc, setMcc] = useState(record?.cardLinkConfig.mcc ?? '');
  const [mid, setMid] = useState(record?.cardLinkConfig.mid ?? '');
  const [isChain, setIsChain] = useState(
    record?.cardLinkConfig.isChain ?? false,
  );
  const [irlEvent, setIrlEvent] = useState(record?.irlEvent ?? false);
  const [pendingToggle, setPendingToggle] = useState(false);
  const [busy, setBusy] = useState(false);

  const canToggle = hasAccess(
    { role, accessibleRegions },
    { capability: 'stores:write' },
  );

  const validate = (): string | null => {
    if (!name.trim()) return 'A venue name is required';
    if (!addressLine.trim()) return 'An address is required';
    if (!postcode.trim()) return 'A postcode is required';
    if (!mcc.trim()) return 'An MCC is required for card-link matching';
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      notify(problem, { type: 'error' });
      return;
    }
    const cardLinkConfig = {
      mcc: mcc.trim(),
      ...(mid.trim() ? { mid: mid.trim() } : {}),
      isChain,
    };
    const resolvedMerchant =
      merchantId === STANDALONE ? null : Number(merchantId);
    const nowIso = new Date().toISOString();
    const shared = {
      name,
      merchantId: resolvedMerchant,
      addressLine,
      postcode,
      country,
      market,
      trackingMethod,
      cardLinkConfig,
      irlEvent,
      updatedAt: nowIso,
    };
    try {
      if (isEdit && record) {
        await dataProvider.update('offlineStores', {
          id: record.id,
          data: shared,
          previousData: record,
        });
        notify('Venue saved', { type: 'success' });
        redirect('list', 'offlineStores');
      } else {
        await dataProvider.create('offlineStores', {
          data: {
            ...shared,
            // A new venue starts active (the deactivate-not-delete flag).
            status: 'active' as OfflineStoreStatus,
            createdBy: String(identity?.email ?? identity?.id ?? 'unknown'),
            createdAt: nowIso,
          },
        });
        notify('Venue created', { type: 'success' });
        redirect('list', 'offlineStores');
      }
    } catch {
      notify('Could not save the venue', { type: 'error' });
    }
  };

  // Flip active <-> inactive through the chokepoint (deactivate-not-delete), audited.
  const confirmToggle = async () => {
    if (!record) return;
    setBusy(true);
    const next: OfflineStoreStatus =
      record.status === 'active' ? 'inactive' : 'active';
    try {
      await performStatusTransition({
        dataProvider,
        role,
        capability: 'stores:write',
        resource: 'offlineStores',
        id: record.id,
        data: { status: next, updatedAt: new Date().toISOString() },
        previousData: record,
      });
      notify(next === 'active' ? 'Venue activated' : 'Venue deactivated', {
        type: 'success',
      });
      setPendingToggle(false);
      refresh();
    } catch {
      notify('Could not change the status', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const isActive = record?.status === 'active';

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEdit ? 'Edit venue' : 'New venue'}
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Venue name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'offline-name' }}
            />
            <TextField
              select
              label="Linked brand"
              value={merchantId}
              onChange={(e) => setMerchantId(e.target.value)}
              helperText="Link an online brand, or leave standalone"
              margin="dense"
              size="small"
              sx={{ minWidth: 220 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'offline-merchant' }}
            >
              <option value={STANDALONE}>None (standalone)</option>
              {(merchants ?? []).map((m) => (
                <option key={m.id} value={String(m.id)}>
                  {m.name}
                </option>
              ))}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Location
          </Typography>
          <TextField
            label="Address"
            value={addressLine}
            onChange={(e) => setAddressLine(e.target.value)}
            margin="dense"
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'offline-address' }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Postcode / ZIP"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'offline-postcode' }}
            />
            <TextField
              label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              helperText="The venue's country"
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'offline-country' }}
            />
            <TextField
              select
              label="Market"
              value={market}
              onChange={(e) => setMarket(e.target.value as Market)}
              margin="dense"
              size="small"
              sx={{ minWidth: 120 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'offline-market' }}
            >
              {marketChoices.map((m) => (
                <option key={m} value={m}>
                  {m.toUpperCase()}
                </option>
              ))}
            </TextField>
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={irlEvent}
                onChange={(e) => setIrlEvent(e.target.checked)}
                data-testid="offline-irl-switch"
              />
            }
            label="IRL event venue (a pop-up or event, not a permanent store)"
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Tracking and card-link config
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Tracking method"
              value={trackingMethod}
              onChange={(e) =>
                setTrackingMethod(e.target.value as TrackingMethod)
              }
              margin="dense"
              size="small"
              sx={{ minWidth: 220 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'offline-tracking' }}
            >
              {OFFLINE_TRACKING_METHODS.map((m) => (
                <option key={m} value={m}>
                  {trackingMethodLabel(m)}
                </option>
              ))}
            </TextField>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The CLO matching identifiers the card networks key on (with the
            venue postcode). Authoring only, the crediting is the backend.
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="MCC"
              value={mcc}
              onChange={(e) => setMcc(e.target.value)}
              helperText="Merchant category code"
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'offline-mcc' }}
            />
            <TextField
              label="MID (optional)"
              value={mid}
              onChange={(e) => setMid(e.target.value)}
              helperText="Acquirer / terminal id"
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'offline-mid' }}
            />
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={isChain}
                onChange={(e) => setIsChain(e.target.checked)}
                data-testid="offline-chain-switch"
              />
            }
            label="Part of a chain"
          />
        </CardContent>
      </Card>

      {isEdit && record && (
        <Card sx={{ mb: 2 }} data-testid="offline-toggle">
          <CardContent>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              <Typography variant="subtitle2">Status</Typography>
              <Chip
                size="small"
                color={isActive ? 'success' : 'default'}
                variant={isActive ? 'filled' : 'outlined'}
                label={isActive ? 'Active' : 'Inactive'}
                data-testid="offline-status-chip"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
              A closed venue is deactivated, never deleted (its card-link
              history is kept).
            </Typography>
            {canToggle && (
              <Button
                variant="contained"
                color={isActive ? 'warning' : 'success'}
                disabled={busy}
                onClick={() => setPendingToggle(true)}
                data-testid="offline-toggle-button"
              >
                {isActive ? 'Deactivate' : 'Activate'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={save} data-testid="offline-save">
          {isEdit ? 'Save' : 'Create venue'}
        </Button>
        <Button onClick={() => redirect('list', 'offlineStores')}>
          Cancel
        </Button>
      </Stack>

      <Confirm
        isOpen={pendingToggle}
        loading={busy}
        title={isActive ? 'Deactivate this venue' : 'Activate this venue'}
        content={
          isActive
            ? 'This hides the venue from the consumer surfaces. You can reactivate it later.'
            : 'This shows the venue on the consumer surfaces.'
        }
        confirm="Confirm"
        cancel="Cancel"
        onConfirm={confirmToggle}
        onClose={() => setPendingToggle(false)}
      />
    </Box>
  );
};

/** Create view. Exported for testing without the access wrapper. */
export const OfflineStoreCreateView = () => <OfflineStoreFormInner />;

/** Edit view. Exported for testing without the access wrapper. */
export const OfflineStoreEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<OfflineStore>('offlineStores', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return <OfflineStoreFormInner record={record} />;
};

export const OfflineStoreCreate = () => (
  <RequireAccess capability="stores:write">
    <OfflineStoreCreateView />
  </RequireAccess>
);

export const OfflineStoreEdit = () => (
  <RequireAccess capability="stores:write">
    <OfflineStoreEditView />
  </RequireAccess>
);
